/**
 * PublishToExecutiveDialog — publish a curated Tornado report to the
 * Executive View's tornado library.
 *
 * Launched from the tornado report view, and only after a chart has been
 * generated in the current view (the admin verifies the rendered answer
 * before anything becomes executive-visible — the same principle as the CV
 * result-card save). The server re-reads and validates the report's
 * Metadata; the saved prompt freezes the definition with the scenario pair
 * stripped into default_* seed keys, so executives late-bind their own
 * Baseline / Compare-to selection at run time.
 */
import React, { useState } from 'react';
import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import PopupDialog from 'src/components/shared/PopupDialog';
import { createTornadoPrompt } from 'src/components/AgenticV2/library/api';

// Seed suggestions only — Category is free text; the library groups by
// whatever categories Trinity authors settle on.
const CATEGORY_SUGGESTIONS = [
  'Revenue & Growth',
  'Patient Flow',
  'Market Share',
  'Scenarios & Risk',
];

interface PublishToExecutiveDialogProps {
  open: boolean;
  reportId: number;
  reportName: string;
  onClose: () => void;
}

const PublishToExecutiveDialog: React.FC<PublishToExecutiveDialogProps> = ({
  open,
  reportId,
  reportName,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeded, setSeeded] = useState(false);

  // Seed the executive-facing title from the report name on first open.
  if (open && !seeded) {
    setTitle(reportName);
    setSeeded(true);
  }

  const reset = () => {
    setTitle('');
    setSubtitle('');
    setCategory('');
    setSaving(false);
    setSeeded(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      await createTornadoPrompt(
        reportId,
        title.trim(),
        subtitle.trim() || null,
        category.trim()
      );
      enqueueSnackbar('Published to the Executive View.', {
        variant: 'success',
      });
      handleClose();
    } catch (e) {
      setSaving(false);
      enqueueSnackbar(
        e instanceof Error
          ? e.message
          : 'Failed to publish to the Executive View.',
        { variant: 'error' }
      );
    }
  };

  return (
    <PopupDialog
      open={open}
      close={handleClose}
      title="Publish to Executive View"
      maxWidth="sm"
      primaryButtons={[
        {
          id: 'publish',
          label: 'Publish',
          onClick: handlePublish,
          disabled: !title.trim() || !category.trim() || saving,
          loading: saving,
          variant: 'contained',
        },
      ]}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Executives pick their own Baseline and Compare-to scenarios; this
          report&apos;s current scenario pair becomes the default selection.
        </Typography>
        <TextField
          label="Executive-facing question"
          placeholder="e.g. What's driving the change in the upside case?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label="Subtitle (optional)"
          placeholder="Short supporting description"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          fullWidth
          inputProps={{ maxLength: 255 }}
        />
        <Autocomplete
          freeSolo
          options={CATEGORY_SUGGESTIONS}
          inputValue={category}
          onInputChange={(_, value) => setCategory(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              required
              inputProps={{ ...params.inputProps, maxLength: 100 }}
            />
          )}
        />
      </Stack>
    </PopupDialog>
  );
};

export default PublishToExecutiveDialog;
